
import React from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { CreditCard, Plus, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePaymentRequests } from '@/hooks/usePaymentRequests';
import { useAuth } from '@/contexts/AuthContext';
import StatusBadge from '@/components/common/StatusBadge';
import { formatDate, formatPaymentMethod } from '@/utils/formatters';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const AuthorPayments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const userId = user?.id || '';
  const { data: paymentRequests, isLoading } = usePaymentRequests(userId);

  const handleNewPaymentRequest = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('AuthorPayments: New payment request button clicked');
    console.log('AuthorPayments: User ID:', userId);
    
    if (!userId) {
      toast({
        title: 'Authentication Error',
        description: 'Please log in to request payments.',
        variant: 'destructive',
      });
      return;
    }
    
    console.log('AuthorPayments: Navigating to payment request page');
    // Use window.location.href for direct navigation
    window.location.href = '/author/payments/request';
  };

  const handleRequestPaymentFromEmpty = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('AuthorPayments: Request payment from empty state');
    window.location.href = '/author/payments/request';
  };

  return (
    <MainLayout allowedRoles={['author']}>
      <PageHeader 
        title="Payment Requests" 
        description="Manage and track your royalty payment requests"
      />
      
      <div className="flex justify-end mb-6">
        <Button 
          onClick={handleNewPaymentRequest}
          className="bg-royalty-600 hover:bg-royalty-700 flex items-center gap-2"
          type="button"
        >
          <Plus className="h-4 w-4" />
          New Payment Request
        </Button>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Payment Request History</CardTitle>
          <CardDescription>Track the status of your payment requests</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">Loading payment requests...</div>
          ) : paymentRequests && paymentRequests.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request ID</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Processed Date</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paymentRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium">
                      {request.id.substring(0, 8)}
                    </TableCell>
                    <TableCell>{formatDate(request.requestDate)}</TableCell>
                    <TableCell>${request.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-gray-500" />
                        {formatPaymentMethod(request.paymentMethod)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={request.status} />
                    </TableCell>
                    <TableCell>
                      {request.processedDate 
                        ? formatDate(request.processedDate) 
                        : '-'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {request.notes || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center py-12 text-center">
              <FileText className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium mb-2">No payment requests yet</h3>
              <p className="text-gray-500 mb-6">
                You haven't made any payment requests yet. Request a payment to receive your royalty earnings.
              </p>
              <Button 
                onClick={handleRequestPaymentFromEmpty}
                className="bg-royalty-600 hover:bg-royalty-700"
                type="button"
              >
                Request Payment
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default AuthorPayments;
